import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { apiClient } from "../../services/apiClient";
import LoadingSpinner from "../common/LoadingSpinner";

const ModelContentSelectionModal = ({
  isOpen,
  onClose,
  onSelect,
  selectedIds = []
}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [localSelectedIds, setLocalSelectedIds] = useState([...selectedIds]);

  useEffect(() => {
    if (isOpen) {
      loadDocuments();
      setLocalSelectedIds([...selectedIds]);
    }
  }, [isOpen, selectedIds]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await apiClient.getModelContentDocuments();
      if (response.success) {
        // Only show completed documents
        const completedDocs = (response.data.documents || []).filter(
          doc => doc.processing_status === 'completed'
        );
        setDocuments(completedDocs);
      }
    } catch (err) {
      setError(err.message || "Failed to load model content documents");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (documentId) => {
    setLocalSelectedIds(prev => {
      if (prev.includes(documentId)) {
        return prev.filter(id => id !== documentId);
      } else {
        return [...prev, documentId];
      }
    });
  };

  const handleSelectAll = () => {
    const filteredDocIds = filteredDocuments.map(doc => doc.document_id);
    if (localSelectedIds.length === filteredDocIds.length) {
      setLocalSelectedIds([]);
    } else {
      setLocalSelectedIds(filteredDocIds);
    }
  };

  const handleApply = () => {
    onSelect(localSelectedIds);
    onClose();
  };

  if (!isOpen) return null;

  const filteredDocuments = documents.filter(doc =>
    doc.document_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FontAwesomeIcon icon="file-contract" className="text-red-500 text-xl" />
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                Reference Model Content
              </h3>
              <p className="text-sm text-slate-600">
                Select exemplar documents to guide AI content generation
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <FontAwesomeIcon icon="times" className="text-xl" />
          </button>
        </div>

        {/* Search and Selection Count */}
        <div className="px-6 py-4 border-b border-gray-200 space-y-3">
          <div className="relative">
            <FontAwesomeIcon
              icon="search"
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              placeholder="Search model documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">
              {localSelectedIds.length} of {filteredDocuments.length} selected
            </span>
            <button
              onClick={handleSelectAll}
              className="text-sm text-red-600 hover:text-red-700 font-medium"
            >
              {localSelectedIds.length === filteredDocuments.length ? "Deselect All" : "Select All"}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" text="Loading model documents..." />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <FontAwesomeIcon icon="exclamation-circle" className="text-4xl text-red-500 mb-4" />
              <p className="text-red-700 mb-4">{error}</p>
              <button
                onClick={loadDocuments}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Retry
              </button>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FontAwesomeIcon icon="file-contract" className="text-4xl text-slate-300 mb-4" />
              <h4 className="text-lg font-medium text-slate-900 mb-2">
                {searchTerm ? "No documents found" : "No model content available"}
              </h4>
              <p className="text-slate-600 mb-4">
                {searchTerm
                  ? "Try adjusting your search terms"
                  : "Upload model content documents in the Admin Panel to get started"
                }
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredDocuments.map((doc) => (
                <div
                  key={doc.document_id}
                  onClick={() => handleToggle(doc.document_id)}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    localSelectedIds.includes(doc.document_id)
                      ? "border-red-500 bg-red-50"
                      : "border-slate-200 hover:border-red-300 hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 pt-0.5">
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          localSelectedIds.includes(doc.document_id)
                            ? "bg-red-500 border-red-500"
                            : "border-slate-300"
                        }`}
                      >
                        {localSelectedIds.includes(doc.document_id) && (
                          <FontAwesomeIcon icon="check" className="text-white text-xs" />
                        )}
                      </div>
                    </div>

                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-slate-900 mb-1">
                            {doc.document_name}
                          </h4>
                          {doc.description && (
                            <p className="text-sm text-slate-600 mb-2">
                              {doc.description}
                            </p>
                          )}
                          <div className="flex items-center space-x-4 text-xs text-slate-500">
                            <span>
                              <FontAwesomeIcon icon="file" className="mr-1" />
                              {doc.original_filename}
                            </span>
                            <span>
                              <FontAwesomeIcon icon="calendar" className="mr-1" />
                              {new Date(doc.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <FontAwesomeIcon
                          icon="file-contract"
                          className={`text-2xl ${
                            localSelectedIds.includes(doc.document_id)
                              ? "text-red-500"
                              : "text-slate-300"
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            {localSelectedIds.length > 0 && (
              <span>{localSelectedIds.length} document{localSelectedIds.length !== 1 ? 's' : ''} will be referenced</span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-700 hover:text-slate-900 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
            >
              <FontAwesomeIcon icon="check" className="mr-2" />
              Apply Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModelContentSelectionModal;
